import React, { useCallback, useEffect, useMemo, useState } from "react";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import ReceivableCustomerCard from "../../../components/Receivables/ReceivableCustomerCard";
import ReceivablesSummaryHero from "../../../components/Receivables/ReceivablesSummaryHero";
import EditReceivableContactModal from "../../../components/Receivables/EditReceivableContactModal";
import { useNavigate } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import { FiInbox } from "react-icons/fi";
import { GetReceivablesOverviewAPI } from "../../../ApiRequests";
import { ErrorToast } from "../../../utils/ShowToast";
import { getAppBasePath } from "../../../utils/appPaths";
import { usePageRefresh } from "../../../hooks/usePageRefresh";

const SORT_OPTIONS = [
  { id: "due_desc", label: "Highest due" },
  { id: "due_asc", label: "Lowest due" },
  { id: "cartons_desc", label: "Most cartons" },
  { id: "name", label: "Name A–Z" },
  { id: "page_asc", label: "Page No" },
  { id: "date_desc", label: "Newest first" },
  { id: "date_asc", label: "Oldest first" },
];

const rowTime = (row) => {
  const t = row?.updatedAt || row?.createdAt;
  if (!t) return 0;
  const ms = new Date(t).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const comparePageNo = (a, b) => {
  const pa = String(a.page_no ?? "").trim();
  const pb = String(b.page_no ?? "").trim();
  const na = Number(pa);
  const nb = Number(pb);
  const aNum = pa !== "" && !Number.isNaN(na);
  const bNum = pb !== "" && !Number.isNaN(nb);
  if (aNum && bNum) return na - nb;
  if (aNum) return -1;
  if (bNum) return 1;
  return pa.localeCompare(pb);
};

const Receivables = () => {
  const [searchText, setSearchText] = useState("");
  const [pageFilter, setPageFilter] = useState("");
  const [sortBy, setSortBy] = useState("due_desc");
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [overall, setOverall] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const navigate = useNavigate();

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetReceivablesOverviewAPI();
      const payload = response?.data?.data?.payload;
      setCustomers(Array.isArray(payload?.customers) ? payload.customers : []);
      setOverall(payload?.overall || null);
    } catch (err) {
      ErrorToast(
        err.response?.data?.error?.msg || "Failed to load receivables"
      );
      setCustomers([]);
      setOverall(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  usePageRefresh(loadOverview);

  const receivableCustomers = useMemo(() => {
    return (customers || []).filter(
      (c) =>
        Number(c.recieveable) > 0 ||
        Number(c.total) > 0 ||
        Number(c.boxQty) > 0 ||
        Number(c.totalPurchaseQty) > 0
    );
  }, [customers]);

  const summary = useMemo(() => {
    if (overall) {
      return {
        count:
          overall.customersWithReceivable ??
          receivableCustomers.filter((c) => Number(c.recieveable) > 0).length,
        total: overall.total ?? 0,
        received: overall.received ?? 0,
        remaining: overall.remaining ?? 0,
        totalCartons: overall.totalCartons ?? 0,
        totalPurchaseQty: overall.totalPurchaseQty ?? 0,
      };
    }
    const withDue = receivableCustomers.filter((c) => Number(c.recieveable) > 0);
    return {
      count: withDue.length,
      total: receivableCustomers.reduce((s, c) => s + Number(c.total || 0), 0),
      received: receivableCustomers.reduce(
        (s, c) => s + Number(c.recieved || 0),
        0
      ),
      remaining: receivableCustomers.reduce(
        (s, c) => s + Number(c.recieveable || 0),
        0
      ),
      totalCartons: receivableCustomers.reduce(
        (s, c) => s + Number(c.boxQty || 0),
        0
      ),
      totalPurchaseQty: receivableCustomers.reduce(
        (s, c) => s + Number(c.totalPurchaseQty || 0),
        0
      ),
    };
  }, [overall, receivableCustomers]);

  const maxDue = useMemo(
    () =>
      receivableCustomers.reduce(
        (m, c) => Math.max(m, Number(c.recieveable || 0)),
        0
      ),
    [receivableCustomers]
  );

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const pq = pageFilter.trim().toLowerCase();
    let list = receivableCustomers;
    if (q) {
      list = list.filter(
        (c) =>
          String(c.name || "").toLowerCase().includes(q) ||
          String(c.contact || "").includes(q) ||
          String(c.contact2 || "").includes(q) ||
          String(c.page_no || "").toLowerCase().includes(q) ||
          String(c.address || "").toLowerCase().includes(q) ||
          (c.purchaseItems || []).some((item) =>
            String(item.name || "").toLowerCase().includes(q)
          )
      );
    }
    if (pq) {
      list = list.filter((c) =>
        String(c.page_no ?? "").toLowerCase().includes(pq)
      );
    }
    const sorted = [...list];
    if (sortBy === "due_desc") {
      sorted.sort(
        (a, b) => Number(b.recieveable || 0) - Number(a.recieveable || 0)
      );
    } else if (sortBy === "due_asc") {
      sorted.sort(
        (a, b) => Number(a.recieveable || 0) - Number(b.recieveable || 0)
      );
    } else if (sortBy === "cartons_desc") {
      sorted.sort((a, b) => Number(b.boxQty || 0) - Number(a.boxQty || 0));
    } else if (sortBy === "page_asc") {
      sorted.sort(comparePageNo);
    } else if (sortBy === "date_desc") {
      sorted.sort((a, b) => rowTime(b) - rowTime(a));
    } else if (sortBy === "date_asc") {
      sorted.sort((a, b) => rowTime(a) - rowTime(b));
    } else {
      sorted.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );
    }
    return sorted;
  }, [receivableCustomers, searchText, pageFilter, sortBy]);

  const base = getAppBasePath();

  const handleCustomerUpdated = (updated) => {
    if (!updated?._id) return;
    setCustomers((prev) =>
      prev.map((c) =>
        c._id === updated._id
          ? { ...c, ...updated, boxQty: c.boxQty, totalPurchaseQty: c.totalPurchaseQty, purchaseItems: c.purchaseItems }
          : c
      )
    );
  };

  const handleEditCustomer = (customer) => {
    setEditCustomer(customer);
    setEditOpen(true);
  };

  return (
    <BodyWrapper>
      <Header
        title="Customer Receivables"
        desc="Outstanding balances, cartons taken & item qty"
      />

      <ReceivablesSummaryHero summary={summary} loading={loading} />

      <div className="flex flex-col max980:gap-3 gap-4 mb-5">
        <div className="flex max980:flex-col gap-3">
          <div className="relative flex-1">
            <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search customer, phone, page, address or item..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-line bg-card text-sm text-ink placeholder:text-muted shadow-soft focus:outline-none focus:border-main focus:ring-2 focus:ring-main/15 transition-all"
            />
          </div>
          <div className="relative max980:w-full w-48 shrink-0">
            <input
              type="text"
              value={pageFilter}
              onChange={(e) => setPageFilter(e.target.value)}
              placeholder="Filter by page no..."
              className="w-full px-4 py-3 rounded-xl border border-line bg-card text-sm text-ink placeholder:text-muted shadow-soft focus:outline-none focus:border-main focus:ring-2 focus:ring-main/15 transition-all"
            />
            {pageFilter && (
              <button
                type="button"
                onClick={() => setPageFilter("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-main text-lg leading-none"
                aria-label="Clear page filter"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {SORT_OPTIONS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setSortBy(id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                sortBy === id
                  ? "bg-main text-white border-main shadow-soft"
                  : "bg-card text-inkSecondary border-line hover:border-main/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!loading && filtered.length > 0 && (
        <p className="text-xs text-muted font-medium mb-3">
          Showing {filtered.length} of {receivableCustomers.length}
          {summary.totalCartons > 0 && (
            <span className="ml-2 text-info">
              · {Number(summary.totalCartons).toLocaleString("en-PK")} cartons
              overall
            </span>
          )}
        </p>
      )}

      {loading ? (
        <FetchingLoading />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/60">
          <div className="w-16 h-16 rounded-2xl bg-brandSoft flex items-center justify-center mb-4">
            <FiInbox className="text-main" size={28} />
          </div>
          <p className="font-bold text-ink text-lg">
            {receivableCustomers.length === 0
              ? "All clear!"
              : "No matches found"}
          </p>
          <p className="text-sm text-muted mt-1 text-center max-w-xs">
            {receivableCustomers.length === 0
              ? "No customer sales or receivables found."
              : "Try a different search term or clear filters."}
          </p>
          {(searchText || pageFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearchText("");
                setPageFilter("");
              }}
              className="mt-4 text-sm font-semibold text-main hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3 pb-4">
          {filtered.map((customer, index) => (
            <ReceivableCustomerCard
              key={customer._id}
              customer={customer}
              maxDue={maxDue}
              rank={index + 1}
              onOpen={() => navigate(`${base}/receivables/${customer._id}`)}
              onEdit={handleEditCustomer}
            />
          ))}
        </div>
      )}

      <EditReceivableContactModal
        open={editOpen}
        setOpen={setEditOpen}
        customer={editCustomer}
        onUpdated={handleCustomerUpdated}
      />
    </BodyWrapper>
  );
};

export default Receivables;
